import React from 'react'
import { css } from '@emotion/core'
import { useWindowSize } from 'react-use'
import { useTranslation } from 'react-i18next'
import { Control, Controller, useFieldArray } from 'react-hook-form'
import { DiscountSettingType, DiscountType } from '@/models/sales'
import { AccountsReceivable, AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { Option, Select } from '@/components/atoms/select'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'

type DiscountEditFormValues = {
  discountSettings: DiscountSettingType[]
}

type Props = {
  control: Control<DiscountEditFormValues>
  accountsReceivableSettings: AccountsReceivableSettingType[]
}
export const SettingDiscountEditForm: React.FC<Props> = ({ control, accountsReceivableSettings }) => {
  const windows = useWindowSize()
  const { t } = useTranslation()
  const discountSettingInput = useFieldArray({
    control,
    name: 'discountSettings',
    keyName: '_key',
    rules: { required: true },
  })

  const onAddDiscountSetting = () => {
    const maxDisplayOrder = discountSettingInput.fields.reduce((max, f) => Math.max(max, f.displayOrder), 0)
    discountSettingInput.append({
      discountSubjectCode: '',
      displayOrder: maxDisplayOrder + 1,
      name: '',
      price: '0',
      type: DiscountType.Custom,
      isAccountsReceivable: AccountsReceivable.AccountsReceivable,
      accountsReceivableCode: '',
      accountsReceivableName: '',
      isPaymentAllocation: true,
    })
  }

  return (
    <>
      <div css={[discountSettingEditHeaderStyle, scrollStyle]}>
        <div css={displayOrderColumnStyle}>{t('Display order')}</div>
        <div css={subjectCodeColumnStyle}>{t('Discount item ID')}</div>
        <div css={nameColumnStyle}>{t('Discount item')}</div>
        <div css={priceColumnStyle}>{t('Amount of money')}</div>
        <div css={isAccountsReceivableColumnStyle}>{t('Outstanding sales')}</div>
        <div css={accountsReceivableNameColumnStyle}>{t('Accounts receivable')}</div>
        <div css={discountTypeColumnStyle}>{t('Discount Type')}</div>
        <div css={deleteButtonColumnStyle}></div>
      </div>
      <div css={scrollStyle} style={{ height: windows.height - 247 }}>
        <ul css={discountSettingEditFormStyle}>
          {discountSettingInput.fields.map((field, index) => {
            const isDefaultDiscount = field.type === DiscountType.Default

            return (
              <li key={field._key}>
                <div css={displayOrderColumnStyle}>
                  <Controller
                    control={control}
                    name={`discountSettings.${index}.displayOrder`}
                    render={({ field: { onChange, value, name } }) => (
                      <input name={name} type="text" placeholder="000" css={inputStyle} value={value || ''} onChange={onChange} />
                    )}
                  />
                </div>

                <div css={subjectCodeColumnStyle}>
                  <Controller
                    control={control}
                    name={`discountSettings.${index}.discountSubjectCode`}
                    rules={{ required: t('Required field has not been entered') }}
                    render={({ field: { onChange, value, name } }) => (
                      <input
                        name={name}
                        type="text"
                        placeholder="0000"
                        css={inputStyle}
                        value={value || ''}
                        onChange={onChange}
                        disabled={!!field.id}
                      />
                    )}
                  />
                </div>

                {isDefaultDiscount ? (
                  <>
                    <div css={[nameColumnStyle, fixedColStyle]}>{field.name}</div>
                    <div css={[priceColumnStyle, fixedColStyle]}>
                      {field.name === 'ポイント' ? '-' : Number(field.price).toLocaleString()}
                    </div>
                  </>
                ) : (
                  <>
                    <div css={nameColumnStyle}>
                      <Controller
                        control={control}
                        name={`discountSettings.${index}.name`}
                        rules={{ required: t('Required field has not been entered') }}
                        render={({ field: { onChange, value, name } }) => (
                          <input
                            name={name}
                            type="text"
                            placeholder={t('Coupon')}
                            css={inputStyle}
                            value={value || ''}
                            onChange={onChange}
                          />
                        )}
                      />
                    </div>
                    <div css={priceColumnStyle}>
                      <Controller
                        control={control}
                        name={`discountSettings.${index}.price`}
                        render={({ field: { onChange, value, name } }) => (
                          <input name={name} type="text" placeholder="0000" css={inputStyle} value={value || ''} onChange={onChange} />
                        )}
                      />
                    </div>
                  </>
                )}

                <div css={isAccountsReceivableColumnStyle}>
                  <Controller
                    control={control}
                    name={`discountSettings.${index}.isAccountsReceivable`}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value || AccountsReceivable.NonAccountsReceivable}
                        onChange={onChange}
                        customStyle={css({ paddingBottom: 0 })}
                      >
                        <Option value={AccountsReceivable.NonAccountsReceivable}>-</Option>
                        <Option value={AccountsReceivable.AccountsReceivable}>{t('Outstanding sales')}</Option>
                      </Select>
                    )}
                  />
                </div>
                <div css={accountsReceivableNameColumnStyle}>
                  <Controller
                    control={control}
                    name={`discountSettings.${index}.accountsReceivableCode`}
                    render={({ field: { onChange, value } }) => (
                      <Select value={value || ''} onChange={onChange} customStyle={css({ paddingBottom: 0 })}>
                        <Option value="">-</Option>
                        {accountsReceivableSettings &&
                          accountsReceivableSettings.map((accountsReceivableSetting, settingIndex) => (
                            <Option key={settingIndex} value={accountsReceivableSetting.code}>
                              {accountsReceivableSetting.name}
                            </Option>
                          ))}
                      </Select>
                    )}
                  />
                </div>
                <div css={discountTypeColumnStyle}>
                  <Controller
                    control={control}
                    name={`discountSettings.${index}.isPaymentAllocation`}
                    render={({ field: { onChange, value } }) => (
                      <Select value={value ? 1 : 0} onChange={v => onChange(!!v)} customStyle={css({ paddingBottom: 0 })}>
                        <Option value={0}>{t('Markdown')}</Option>
                        <Option value={1}>{t('Payment Allocation')}</Option>
                      </Select>
                    )}
                  />
                </div>

                {isDefaultDiscount ? (
                  <div css={deleteButtonColumnStyle}></div>
                ) : (
                  <DeleteIcon onClick={() => discountSettingInput.remove(index)} />
                )}
              </li>
            )
          })}
        </ul>
        <div css={plusButtonRowStyle} onClick={onAddDiscountSetting}>
          <div>
            <img src={require('@/static/images/plus.svg')} alt={t('Add Discount Item')} />
            <p>{t('Add Discount Item')}</p>
          </div>
        </div>
      </div>
    </>
  )
}
const displayOrderColumnStyle = css({ minWidth: 80, maxWidth: 80, paddingRight: 16 })
const subjectCodeColumnStyle = css({ minWidth: '12%', maxWidth: '12%', paddingRight: 16 })
const nameColumnStyle = css({ flexGrow: 1, paddingRight: 16 })
const priceColumnStyle = css({ minWidth: '10%', maxWidth: '10%', paddingRight: 16 })
const isAccountsReceivableColumnStyle = css({ minWidth: 110, maxWidth: 110, paddingRight: 16 })
const accountsReceivableNameColumnStyle = css({ minWidth: '20%', maxWidth: '20%', paddingRight: 16 })
const discountTypeColumnStyle = css({ minWidth: 120, maxWidth: 120, paddingRight: 16 })
const deleteButtonColumnStyle = css({ minWidth: 30, maxWidth: 30 })
const fixedColStyle = css({
  height: 32,
  alignContent: 'center',
  paddingLeft: 16,
})

const discountSettingEditHeaderStyle = css({
  background: '#F5F5F5',
  color: '#676767',
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  padding: '10px 32px',
})

const discountSettingEditFormStyle = css({
  padding: '24px 32px 12px',
  li: {
    display: 'flex',
    padding: '0 0 16px 0',
    fontSize: 14,
  },
})
const inputStyle = css({
  width: '100%',
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  padding: '0 16px',
  '&::placeholder': { color: '#CCCCCC' },
})

const plusButtonRowStyle = css({
  padding: '0px 32px 23px',
  div: {
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    cursor: 'pointer',
    p: {
      color: '#F2A40B',
      fontSize: 12,
      paddingLeft: 10,
    },
    img: {
      width: 20,
      height: 20,
    },
  },
})
const scrollStyle = css({ 'overflow-y': 'scroll' })
